/**
 * CreateStudyModal — the create-study form (plan §5.3, §7.5, §13.4 WP-34
 * frontend scope: "a create-study form using the api client that shows the
 * 409 candidates when a tag text is unresolved and lets the user pick one
 * (sending the entry id) or keep typing").
 */
import { useState, FormEvent } from 'react';
import { Modal, primaryBtnCls, secondaryBtnCls, fieldCls, FilterSelect, useToast } from '../../../ui';
import { createStudy } from '../../api/client';
import type { Study, StudyInput, TagUnresolvedErrorBody, TagMatchCandidate } from '../../api/client';
import { TagField, EMPTY_TAG_FIELD } from './TagField';
import type { TagFieldState } from './TagField';

type TagKey = 'sponsor' | 'cro' | 'therapeuticArea' | 'indication';

const TAG_FIELD_DEFS: Array<{ key: TagKey; label: string }> = [
  { key: 'sponsor', label: 'Sponsor' },
  { key: 'cro', label: 'CRO' },
  { key: 'therapeuticArea', label: 'Therapeutic area' },
  { key: 'indication', label: 'Indication' },
];

const RECRUITMENT_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: '1', label: '1 — easiest' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — hardest' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (study: Study) => void;
}

export function CreateStudyModal({ isOpen, onClose, onCreated }: Props) {
  const { toast } = useToast();
  const [protocolNumber, setProtocolNumber] = useState('');
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('open');
  const [recruitmentDifficulty, setRecruitmentDifficulty] = useState('');
  const [enrollmentTarget, setEnrollmentTarget] = useState('');
  const [tagFields, setTagFields] = useState<Record<TagKey, TagFieldState>>({
    sponsor: EMPTY_TAG_FIELD,
    cro: EMPTY_TAG_FIELD,
    therapeuticArea: EMPTY_TAG_FIELD,
    indication: EMPTY_TAG_FIELD,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setProtocolNumber('');
    setTitle('');
    setStatus('open');
    setRecruitmentDifficulty('');
    setEnrollmentTarget('');
    setTagFields({ sponsor: EMPTY_TAG_FIELD, cro: EMPTY_TAG_FIELD, therapeuticArea: EMPTY_TAG_FIELD, indication: EMPTY_TAG_FIELD });
    setFormError(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function updateTagText(key: TagKey, text: string) {
    setTagFields((prev) => ({ ...prev, [key]: { text, resolvedId: null, candidates: null } }));
  }

  function pickCandidate(key: TagKey, candidate: TagMatchCandidate) {
    setTagFields((prev) => ({
      ...prev,
      [key]: { text: candidate.entry.official_name, resolvedId: candidate.entry.id, candidates: null },
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!protocolNumber.trim()) {
      setFormError('Protocol number is required.');
      return;
    }

    const tagValues: Partial<Record<TagKey, string>> = {};
    for (const { key } of TAG_FIELD_DEFS) {
      const field = tagFields[key];
      const value = field.resolvedId ?? field.text.trim();
      if (value) tagValues[key] = value;
    }
    const input: StudyInput = {
      protocolNumber: protocolNumber.trim(),
      status,
      ...(title.trim() ? { title: title.trim() } : {}),
      ...(recruitmentDifficulty ? { recruitmentDifficulty: Number(recruitmentDifficulty) } : {}),
      ...(enrollmentTarget ? { enrollmentTarget: Number(enrollmentTarget) } : {}),
      ...tagValues,
    };

    setSubmitting(true);
    setFormError(null);
    try {
      const study = await createStudy(input);
      toast(`Study ${study.protocol_number} created.`, { type: 'success' });
      onCreated(study);
      handleClose();
    } catch (err) {
      // Narrow structurally (no axios import here — this repo's Jest cannot
      // parse axios's ESM build unmocked; every module under src/contracts
      // reaches it only through the mocked services/api, never directly).
      const res = (err as { response?: { status?: number; data?: any } })?.response;
      if (!res) {
        setFormError('Something went wrong creating the study.');
      } else if (res.status === 409 && Array.isArray(res.data?.candidates)) {
        const body = res.data as TagUnresolvedErrorBody;
        setTagFields((prev) => ({ ...prev, [body.field]: { ...prev[body.field], candidates: body.candidates } }));
        setFormError(`Could not resolve "${TAG_FIELD_DEFS.find((f) => f.key === body.field)?.label ?? body.field}" — pick a match below, or keep typing.`);
      } else if (typeof res.data?.error === 'string') {
        setFormError(res.data.error);
      } else {
        setFormError('Something went wrong creating the study.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="New study">
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="cta-protocol-number" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Protocol number
            </label>
            <input
              id="cta-protocol-number"
              type="text"
              value={protocolNumber}
              onChange={(e) => setProtocolNumber(e.target.value)}
              className={`${fieldCls} w-full`}
              autoFocus
            />
          </div>
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Status</span>
            <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          </div>
        </div>

        <div>
          <label htmlFor="cta-title" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
            Title
          </label>
          <input id="cta-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={`${fieldCls} w-full`} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {TAG_FIELD_DEFS.map(({ key, label }) => (
            <TagField
              key={key}
              id={`cta-tag-${key}`}
              label={label}
              state={tagFields[key]}
              onTextChange={(text) => updateTagText(key, text)}
              onPick={(candidate) => pickCandidate(key, candidate)}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Recruitment difficulty</span>
            <FilterSelect
              label="Recruitment difficulty"
              value={recruitmentDifficulty}
              onChange={setRecruitmentDifficulty}
              options={RECRUITMENT_OPTIONS}
            />
          </div>
          <div>
            <label htmlFor="cta-enrollment" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Enrollment target
            </label>
            <input
              id="cta-enrollment"
              type="number"
              min={0}
              value={enrollmentTarget}
              onChange={(e) => setEnrollmentTarget(e.target.value)}
              className={`${fieldCls} w-full`}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" onClick={handleClose} className={secondaryBtnCls}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} className={primaryBtnCls}>
            {submitting ? 'Creating…' : 'Create study'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default CreateStudyModal;
